// React hooks for patient data
import { useState, useEffect } from 'react';
import { patientService } from '../services/patientService';
import { riskAssessmentEngine } from '../services/riskAssessmentEngine';
import { useOrg } from './useOrg';

/**
 * Hook to get all patients with optional filters
 */
export const usePatients = (filters = {}) => {
  const { activeOrgId } = useOrg();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPatients = async () => {
    try {
      setLoading(true);
      setError(null);

      let data;

      if (filters.departmentId) {
        data = await patientService.getPatientsByDepartment(filters.departmentId);
      } else if (filters.roomId) {
        data = await patientService.getPatientsByRoom(filters.roomId);
      } else if (filters.bedId) {
        const patient = await patientService.getPatientByBed(filters.bedId);
        data = patient ? [patient] : [];
      } else if (filters.status) {
        data = await patientService.getPatientsByStatus(filters.status, activeOrgId);
      } else if (filters.riskFactorId) {
        data = await patientService.getPatientsWithRiskFactor(filters.riskFactorId, activeOrgId);
      } else if (filters.highRisk) {
        data = await patientService.getHighRiskPatients(filters.minimumRiskFactors || 2, activeOrgId);
      } else {
        data = await patientService.getAllPatients(activeOrgId);
      }

      setPatients(data);
    } catch (err) {
      console.error('Error loading patients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, [
    activeOrgId,
    filters.departmentId,
    filters.roomId,
    filters.bedId,
    filters.status,
    filters.riskFactorId,
    filters.highRisk,
    filters.minimumRiskFactors
  ]);

  const refresh = () => {
    loadPatients();
  };

  return { patients, loading, error, refresh };
};

/**
 * Hook to get a single patient by ID
 */
export const usePatient = (patientId) => {
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPatient = async () => {
    if (!patientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await patientService.getPatient(patientId);
      setPatient(data);
    } catch (err) {
      console.error('Error loading patient:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatient();
  }, [patientId]);

  const refresh = () => {
    loadPatient();
  };

  return { patient, loading, error, refresh };
};

/**
 * Hook for patient mutations (create, update, delete)
 */
export const usePatientMutations = () => {
  const { activeOrgId } = useOrg();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Trigger risk assessment for a patient
   */
  const triggerPatientRiskAssessment = async (patientId, patientData) => {
    try {
      // Calculate base risk from patient risk factors
      const baseRisk = (patientData.riskFactorIds?.length || 0) * 100; // Simple base calculation

      const context = {
        patient: {
          id: patientId,
          name: patientData.patientIdentifier,
          departmentId: patientData.departmentId,
          roomId: patientData.roomId,
          bedId: patientData.bedId,
          riskFactorIds: patientData.riskFactorIds || []
        },
        department: {
          id: patientData.departmentId,
          name: 'Department' // TODO: Get actual department name
        },
        room: {
          id: patientData.roomId,
          name: 'Room' // TODO: Get actual room name
        },
        bed: {
          id: patientData.bedId,
          name: 'Bed' // TODO: Get actual bed name
        }
      };

      console.log('🚨 Triggering risk assessment with base risk:', baseRisk, 'context:', context);
      await riskAssessmentEngine.calculateComprehensiveRisk(baseRisk, context);
    } catch (error) {
      console.error('Error triggering patient risk assessment:', error);
    }
  };

  const createPatient = async (patientData, userId) => {
    try {
      setSaving(true);
      setError(null);
      // Inject organizationId so the document is queryable by org-scoped filters.
      // PatientForm does not set organizationId — always injected here from OrgContext.
      const dataWithOrg = { ...patientData, organizationId: activeOrgId };
      const patientId = await patientService.createPatient(dataWithOrg, userId);

      // Trigger risk assessment and alerts
      console.log('🔄 Patient created, triggering risk assessment...');
      await triggerPatientRiskAssessment(patientId, dataWithOrg);

      return patientId;
    } catch (err) {
      console.error('Error creating patient:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const updatePatient = async (patientId, updates, userId) => {
    try {
      setSaving(true);
      setError(null);
      await patientService.updatePatient(patientId, updates, userId);

      // Trigger risk assessment and alerts
      console.log('🔄 Patient updated, triggering risk assessment...');
      await triggerPatientRiskAssessment(patientId, updates);
    } catch (err) {
      console.error('Error updating patient:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const deletePatient = async (patientId) => {
    try {
      setSaving(true);
      setError(null);
      await patientService.deletePatient(patientId);
    } catch (err) {
      console.error('Error deleting patient:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const dischargePatient = async (patientId, userId, dischargeNotes = '') => {
    try {
      setSaving(true);
      setError(null);
      await patientService.dischargePatient(patientId, userId, dischargeNotes);
    } catch (err) {
      console.error('Error discharging patient:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const transferPatient = async (patientId, newBedId, newRoomId, newDepartmentId, userId, transferReason = '') => {
    try {
      setSaving(true);
      setError(null);
      await patientService.transferPatient(patientId, newBedId, newRoomId, newDepartmentId, userId, transferReason);
    } catch (err) {
      console.error('Error transferring patient:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const addRiskFactor = async (patientId, riskFactorId, userId) => {
    try {
      setSaving(true);
      setError(null);
      await patientService.addRiskFactor(patientId, riskFactorId, userId);
    } catch (err) {
      console.error('Error adding risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const removeRiskFactor = async (patientId, riskFactorId, userId) => {
    try {
      setSaving(true);
      setError(null);
      await patientService.removeRiskFactor(patientId, riskFactorId, userId);
    } catch (err) {
      console.error('Error removing risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const updateRiskFactors = async (patientId, riskFactorIds, userId) => {
    try {
      setSaving(true);
      setError(null);
      await patientService.updateRiskFactors(patientId, riskFactorIds, userId);
    } catch (err) {
      console.error('Error updating risk factors:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return {
    createPatient,
    updatePatient,
    deletePatient,
    dischargePatient,
    transferPatient,
    addRiskFactor,
    removeRiskFactor,
    updateRiskFactors,
    saving,
    error
  };
};

/**
 * Hook to get patient statistics
 */
export const usePatientStatistics = (departmentId = null) => {
  const { activeOrgId } = useOrg();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await patientService.getPatientStatistics(departmentId, activeOrgId);
      setStatistics(data);
    } catch (err) {
      console.error('Error loading patient statistics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, [departmentId, activeOrgId]);

  const refresh = () => {
    loadStatistics();
  };

  return { statistics, loading, error, refresh };
};
